// FastAPI reports request-validation failures (422) with `detail` as a LIST of {loc, msg, type},
// and this backend raises such a list explicitly in places (e.g. the send-email parser). Pages
// render `error.response.data.detail` directly (165 places), so a list reaching a JSX child throws
// "Objects are not valid as a React child" and the route ErrorBoundary replaces the whole page
// with its generic panel. Flatten a list to a readable string; leave strings and objects (for example the 409
// duplicate-contact payload that TicketDetail reads on purpose) untouched, and keep the original
// list on `detail_errors` for anything that wants it.
export function flattenValidationDetail(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.detail)) return data;
  const text = data.detail
    .map((item) => {
      if (typeof item === "string") return item;
      const field = Array.isArray(item?.loc)
        ? item.loc.filter((part) => !["body", "query", "path"].includes(part)).join(".")
        : "";
      const message = typeof item?.msg === "string" ? item.msg : "invalid value";
      return field ? `${field}: ${message}` : message;
    })
    .filter(Boolean)
    .join("; ");
  return { ...data, detail: text || "בקשה לא תקינה", detail_errors: data.detail };
}

// Reason codes the backend returns as `detail: {code}` (memory policy refusals) -> a sentence a
// technician can act on. The text never includes what was submitted (it may have been a secret).
const DETAIL_CODE_MESSAGES = {
  secret_material: "הטקסט נראה כמו סיסמה, מפתח או טוקן ולכן לא נשמר. הסירו את הסוד ונסו שוב.",
  content_too_long: "הטקסט ארוך מדי לשמירה בזיכרון הלקוח.",
  empty_content: "אין טקסט לשמירה.",
};

// `error.response.data.detail` as text that is always safe to render as a JSX child: a string is
// kept, `{message}` (the 409 duplicate-contact payload) shows its message, `{code}` shows a known
// sentence, anything else falls back. Never returns an object.
export function detailText(detail, fallback = "הפעולה נכשלה") {
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.code === "string" && detail.code) {
      return DETAIL_CODE_MESSAGES[detail.code] || `${fallback} (${detail.code})`;
    }
  }
  return fallback;
}
